import React, {
  useEffect,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import {
  ICreateEditGaiaobjectFormProps,
  UpdateGaiaObjectRequest,
  GaiaObjectFormRef,
} from "./definitions";
import styles from "./style.module.scss";
import { get, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTeamNameSelector } from "pages/team_settings/hooks";
import { useNotifications } from "features/notifications";
import { useNavigate } from "react-router-dom";
import {
  Button,
  ColumnLayout,
  Container,
  FormField,
  Header,
  SpaceBetween,
  Spinner,
  Form,
  Box,
  Link,
} from "@amzn/awsui-components-react";
import GaiaObjectName from "components/gaiaobject-name";
import DynamicFormField from "components/dynamic-form-field";
import { CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME } from "models/generateCustomAttributesSchema";
import {
  transformScanResultToFormData,
  getNavigationPath,
  buildGaiaObjectProps,
  buildFormResetData,
  buildDraftFormResetData,
  extractUniqueGroups,
  AttributeGroupRenderer,
  getGaiaObjectErrorMessage,
} from "./util";
import { z } from "zod";
import {
  generateDynamicAttributesDataMap,
  transformCustomAttributesDataMap,
} from "utils/dynamicAttributesUtils";
import {
  useCreateGaiaObjectMutation,
  useUpdateGaiaObjectMutation,
} from "./endpoints";
import { useUpdateScanJobMutation } from "pages/drafts/endpoint";
import { registerScanDocument, updateScanStatus } from "utils/scan-job-utils";
import {
  cap,
  getFileExtension,
  getFileNameWithoutExtension,
  getFileSize,
  pluralize,
} from "utils/date-format";
import { getGaiaobjectName } from "components/gaiaobject-name/GaiaobjectName";
import { useGetGaiaObjectQuery } from "pages/gaia-object-overview/endpoints";
import {
  ControlledInput,
  ControlledSelect,
  ControlledMultiselect,
} from "@amzn/react-hook-form-polaris";
import {
  useGetScanResultQuery,
  useGetScanjobQuery,
} from "pages/draft-overview/endpoints";
import {
  useRegisterDocumentMutation,
  useUploadUrlMutation,
} from "components/create-audit-form/endpoints";
import {
  RegisterDocumentRequest,
  UploadUrlRequest,
  UploadUrlResponse,
} from "components/create-audit-form/definitions";
import { useGaiaObjectUiContent } from "features/ui-content";
import { getFilteredOptions } from "features/ui-content/utils";
import { validateAndResetGovEntity } from "utils/gaiaobject-utils";
import UploadFile from "components/upload-file/UploadFile";
import { useTeamSpecificGaiaObjectTemplates } from "components/gaiaobjects-table/table-config";
import PermissionBoundary from "components/permission-boundary";
import { useGaiaObjectCustomAttributes } from "hooks/useGaiaObjectCustomAttributes";
import { useCheckTeamSwitch } from "hooks/useCheckTeamSwitch";
import { useGuestsLoader } from "hooks/useGuestsLoader";
import { useGetContactsListQuery } from "components/contacts-table/endpoints";
import { GaiaConstants } from "utils";
import { useLocalStorage } from "hooks/useLocalStorage";

const CreateEditGaiaobjectForm = forwardRef<
  GaiaObjectFormRef,
  ICreateEditGaiaobjectFormProps
>(
  (
    {
      type,
      mode,
      shortId,
      wizardMode = false,
      onWizardValidationSuccess,
      onWizardValidationError,
      triggerWizardValidation = 0,
    },
    ref
  ) => {
    const { addNotification } = useNotifications();
    const { teamName } = useTeamNameSelector();
    const navigate = useNavigate();
    const defaultErrorMessage = `Failed to ${
      mode === "edit" ? "update" : "create"
    } the ${type}`;
    const [createGaiaObjectCall] = useCreateGaiaObjectMutation();
    const [updateGaiaObjectCall] = useUpdateGaiaObjectMutation();
    const [updateScanJobCall] = useUpdateScanJobMutation();
    const [registerDocumentCall] = useRegisterDocumentMutation();
    const [uploadUrlCall] = useUploadUrlMutation();
    const [disableCreateEdit, setDisableCreateEdit] = React.useState(false);
    const [files, setFiles] = useState<File[]>([]);
    const [isEditModeInit, setIsEditModeInit] = React.useState(mode === "edit");
    const uiContent = useGaiaObjectUiContent(type);
    const defaultCountry: string =
      uiContent?.defaultCountry ?? uiContent?.countryOptions[0]?.value ?? "";

    const { data, error, isLoading, isError } = useGetGaiaObjectQuery(
      {
        gaiaObjectId: shortId!,
      },
      {
        refetchOnMountOrArgChange: true,
        skip: !shortId || mode !== "edit",
      }
    );

    const {
      data: scanResultData,
      error: scanResultError,
      isLoading: scanResultLoading,
      isError: scanResultIsError,
    } = useGetScanResultQuery(
      { shortId: shortId! },
      { refetchOnMountOrArgChange: true, skip: mode !== "draft" }
    );

    const {
      data: scanJobData,
      error: scanJobError,
      isLoading: scanJobLoading,
      isError: scanJobIsError,
    } = useGetScanjobQuery(
      { shortId: shortId! },
      { refetchOnMountOrArgChange: true, skip: mode !== "draft" }
    );

    const gaiaObjectData = mode == "edit" ? data : undefined;
    const isLoadingGaiaObjectData = mode == "edit" ? isLoading : undefined;
    useCheckTeamSwitch(gaiaObjectData?.gaiaObject?.teamName || "");
    const { customAttributes, createSchema, formSchema } =
      useGaiaObjectCustomAttributes(
        type,
        gaiaObjectData,
        isLoadingGaiaObjectData
      );
    useGuestsLoader(mode === "draft" ? "" : shortId || "");

    const transformedGaiaObject =
      customAttributes && data
        ? transformCustomAttributesDataMap(
            data.gaiaObject,
            customAttributes!,
            "YYYY-MM-DD"
          )
        : null;

    const effectiveScanResultData = useMemo(
      () => (mode === "draft" ? scanResultData : null),
      [mode, scanResultData]
    );

    const localStorageKey = `notice-draft-wizard-step1-${shortId}`;
    const [localFormData, setLocalFormData] = useLocalStorage<any>(
      localStorageKey,
      null
    );

    const { handleSubmit, control, formState, watch, reset, setValue } =
      useForm<any>({
        mode: "onBlur",
        resolver: zodResolver(formSchema),
        defaultValues:
          mode === "create"
            ? {
                [CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME]: {
                  country: defaultCountry,
                },
              }
            : {},
      });

    useEffect(() => {
      if (mode === "edit" && transformedGaiaObject) {
        reset(buildFormResetData(transformedGaiaObject));
        setIsEditModeInit(false);
      } else if (
        mode === "draft" &&
        effectiveScanResultData &&
        customAttributes
      ) {
        const scanFormData = transformScanResultToFormData(
          effectiveScanResultData.scanResult,
          customAttributes,
          uiContent
        );
        if (wizardMode && localFormData && shortId) {
          const mergedData = {
            ...buildDraftFormResetData(scanFormData),
            ...localFormData,
          };
          reset(mergedData);
        } else {
          reset(buildDraftFormResetData(scanFormData));
        }
      } else if (
        wizardMode &&
        localFormData &&
        shortId &&
        !effectiveScanResultData
      ) {
        reset(localFormData);
      } else if (mode === "create" && defaultCountry) {
        setValue(
          `${CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME}.country`,
          defaultCountry
        );
      }
    }, [
      mode,
      transformedGaiaObject?.gaiaObjectId,
      effectiveScanResultData,
      defaultCountry,
      uiContent,
      setValue,
      wizardMode,
      localFormData,
      shortId,
      customAttributes,
    ]);

    // The Government entity field must be reactive to the user's Country selection.
    const country = watch(`${CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME}.country`);

    const filteredGovEntityOptions = useMemo(() => {
      return getFilteredOptions(
        uiContent?.governmentEntityOptions || [],
        uiContent?.countryOptions || [],
        "country",
        country
      );
    }, [uiContent?.governmentEntityOptions, country]);

    const governmentEntity = watch(
      `${CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME}.governmentEntity`
    );

    useEffect(() => {
      validateAndResetGovEntity(
        country,
        governmentEntity,
        filteredGovEntityOptions,
        isEditModeInit,
        setValue,
        CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME
      );
    }, [country, filteredGovEntityOptions, governmentEntity, isEditModeInit]);

    const localJurisdiction = watch(
      `${CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME}.localJurisdiction`
    );
    const entity = watch(`${CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME}.entity`);
    const noticeType = watch(
      `${CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME}.noticeType`
    );
    const litigationType = watch(
      `${CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME}.litigationType`
    );
    const taxPeriodStartDate = watch(
      `${CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME}.taxPeriodStart`
    );
    const taxPeriodEndDate = watch(
      `${CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME}.taxPeriodEnd`
    );
    const titleSuffix = watch("titleSuffix");
    const template = watch("template");
    const taxTypes = watch(`${CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME}.taxType`);

    const suffixSourceField = uiContent?.suffixField
      ? watch(`${CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME}.${uiContent.suffixField}`)
      : watch(`${uiContent.suffixField}`);

    useEffect(() => {
      if (uiContent?.suffixField && suffixSourceField) {
        const fieldName = uiContent.suffixField;
        let formattedValue = suffixSourceField;

        if (
          fieldName.toLowerCase().includes("date") ||
          fieldName.toLowerCase().includes("period")
        ) {
          const date = new Date(suffixSourceField);

          if (!isNaN(date.getTime())) {
            formattedValue = date
              .toISOString()
              .split("T")[0]
              .replace(/-/g, "/");
          }
        }

        if (formattedValue !== titleSuffix) {
          setValue("titleSuffix", formattedValue, { shouldValidate: true });
        }
      }
    }, [suffixSourceField, uiContent?.suffixField, setValue]);

    const getTemplatesByCategory = useTeamSpecificGaiaObjectTemplates(type);

    const { data: contactsList } = useGetContactsListQuery(
      { teamName: teamName },
      {
        refetchOnFocus: true,
        skip: !teamName || teamName === GaiaConstants.PLACE_HOLDER,
      }
    );

    const buildWatchedFieldsData = () => ({
      country,
      governmentEntity,
      localJurisdiction,
      entity,
      noticeType,
      taxPeriodStartDate,
      taxPeriodEndDate,
      litigationType,
      taxTypes,
    });

    const buildGaiaObjectPropsFromFields = () => {
      const watchedFields = buildWatchedFieldsData();
      return buildGaiaObjectProps(
        watchedFields,
        type,
        titleSuffix,
        uiContent.entityOptions
      );
    };

    const prevTriggerWizardValidationRef = useRef(triggerWizardValidation);

    useEffect(() => {
      if (
        triggerWizardValidation > 0 &&
        triggerWizardValidation > prevTriggerWizardValidationRef.current
      ) {
        handleSubmit(onValid, onInvalid)();
      }

      prevTriggerWizardValidationRef.current = triggerWizardValidation;
    }, [triggerWizardValidation]);

    useImperativeHandle(ref, () => ({
      getFormData: () => {
        const formData = watch();
        if (wizardMode && shortId) {
          setLocalFormData(formData);
        }
        return formData;
      },
      submitForm: () => {
        handleSubmit(onValid, onInvalid)();
      },
    }));

    const onValid = (data: any, e: any) => {
      if (onWizardValidationSuccess) {
        onWizardValidationSuccess(data);
        return;
      }

      setDisableCreateEdit(true);
      if (mode === "create" && type === "notice" && files[0]) {
        onCreateGaiaObjectWithDoc(data);
      } else {
        createEditGaiaObject(data);
      }
    };

    const onInvalid = (data: any, e: any) => {
      if (onWizardValidationError) {
        onWizardValidationError(formState.errors);
        return;
      }
    };

    const processGaiaObjectData = (object: z.infer<typeof createSchema>) => {
      const gaiaobjectProps = buildGaiaObjectPropsFromFields();
      object.title = getGaiaobjectName(gaiaobjectProps);
      object.teamName = teamName;
      object.category = type;
      object.templateId = template;

      if (customAttributes) {
        object.customAttributesDataMap = generateDynamicAttributesDataMap(
          customAttributes,
          object[CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME]
        );
      }
      return object;
    };

    const updateGaiaObject = (object: z.infer<typeof createSchema>) => {
      const updatedObject = { ...transformedGaiaObject };
      Object.assign(updatedObject, object);

      const gaiaobjectProps = buildGaiaObjectPropsFromFields();

      updatedObject.gaiaObjectId = transformedGaiaObject.gaiaObjectId;
      updatedObject.title = getGaiaobjectName(gaiaobjectProps);
      updatedObject.teamName = teamName;
      updatedObject.category = type;
      updatedObject.shortId = shortId;

      if (customAttributes) {
        updatedObject.customAttributesDataMap =
          generateDynamicAttributesDataMap(
            customAttributes,
            updatedObject[CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME]
          );
      }

      updatedObject[CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME] = {
        ...updatedObject[CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME],
        effort: transformedGaiaObject?.customAttributesDataMap?.effort,
        status: transformedGaiaObject?.customAttributesDataMap?.status,
        statusActionOwner:
          transformedGaiaObject?.customAttributesDataMap?.statusActionOwner,
        statusDetail:
          transformedGaiaObject?.customAttributesDataMap?.statusDetail,
      };

      return updatedObject;
    };

    const updateGaiaObjectRequest = (object: z.infer<typeof createSchema>) => {
      const updatedGaiaObject = updateGaiaObject(object);

      const request: UpdateGaiaObjectRequest = {
        gaiaObjectId: updatedGaiaObject.gaiaObjectId,
        gaiaObject: updatedGaiaObject,
      };

      return request;
    };

    const onCreateGaiaObjectWithDoc = async (
      gaiaObject: z.infer<typeof createSchema>
    ) => {
      let gaiaObjectResponse: any = null;
      try {
        gaiaObjectResponse = await createGaiaObjectCall(
          processGaiaObjectData(gaiaObject)
        ).unwrap();
      } catch (e: any) {
        const errorMessage = getGaiaObjectErrorMessage(
          e,
          type,
          defaultErrorMessage
        );

        addNotification({
          type: "error",
          header: errorMessage,
          dismissible: true,
          timeout: 10000,
        });
        setDisableCreateEdit(false);
        return;
      }

      try {
        let request: UploadUrlRequest = {
          prefix: `${pluralize(type)}/${gaiaObjectResponse.shortId}`,
          fileName: files[0].name,
          teamName: teamName,
          securityLevel: "Normal",
        };
        const response: UploadUrlResponse = await uploadUrlCall(
          request
        ).unwrap();

        await fetch(response.url, {
          method: "PUT",
          body: files[0],
          headers: {
            "x-amz-expected-bucket-owner": response.bucketOwner,
          },
        });

        const s3Prefix = `${pluralize(type)}/${gaiaObjectResponse.shortId}/${
          response.uniqueKey
        }/${files[0].name}`;
        const registerDocumentRequest: RegisterDocumentRequest = {
          parentId: gaiaObjectResponse.shortId,
          gaiaObjectId: gaiaObjectResponse.shortId,
          teamName: teamName,
          fileName: getFileNameWithoutExtension(files[0].name),
          documentType: "Notice letters",
          securityLevel: "Normal",
          s3Prefix: s3Prefix,
          extentionType: getFileExtension(files[0].name),
          size: getFileSize(files[0].size),
        };
        await registerDocumentCall(registerDocumentRequest).unwrap();

        addNotification({
          type: "success",
          header: `${cap(type)} with document created successfully`,
          dismissible: true,
          timeout: 10000,
        });
        navigate(getNavigationPath("create", type, shortId));
      } catch (e: any) {
        addNotification({
          type: "warning",
          header: `${cap(type)} created successfully, but file upload failed`,
          content: `The ${type} was created, but the document could not be uploaded. You can upload the document later from the ${type} details page.`,
          dismissible: true,
          timeout: 15000,
        });
        navigate(getNavigationPath("create", type, shortId));
      }
    };

    const createEditGaiaObject = async (
      gaiaObject: z.infer<typeof createSchema>
    ) => {
      try {
        const response =
          mode === "create" || mode === "draft"
            ? await createGaiaObjectCall(
                processGaiaObjectData(gaiaObject)
              ).unwrap()
            : await updateGaiaObjectCall(
                updateGaiaObjectRequest(gaiaObject)
              ).unwrap();

        if (mode === "draft" && shortId) {
          await registerScanDocument(
            shortId,
            scanJobData,
            response.shortId,
            teamName,
            registerDocumentCall
          );

          await updateScanStatus(shortId, response.shortId, updateScanJobCall);
        }

        addNotification({
          type: "success",
          header: `${cap(type)} ${
            mode === "edit" ? "updated" : "created"
          } successfully`,
          dismissible: true,
          timeout: 10000,
        });

        const successNavigationMode = mode === "edit" ? "edit" : "create";
        navigate(getNavigationPath(successNavigationMode, type, shortId));
      } catch (e: any) {
        const errorMessage = getGaiaObjectErrorMessage(
          e,
          type,
          defaultErrorMessage
        );

        addNotification({
          type: "error",
          header: errorMessage,
          dismissible: true,
          timeout: 10000,
        });

        setDisableCreateEdit(false);
      }
    };

    const onCancelCreate = (event: any) => {
      navigate(getNavigationPath(mode, type, shortId));
    };

    return (
      <PermissionBoundary
        allowMemberOnly={false}
        shouldCheckPermission={true}
        isLoading={isLoading || scanResultLoading || scanJobLoading}
        isError={isError || scanResultIsError || scanJobIsError}
        error={error || scanResultError || scanJobError}
      >
        <form onSubmit={handleSubmit(onValid, onInvalid)}>
          <Form
            actions={
              wizardMode ? undefined : (
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    formAction="none"
                    onClick={onCancelCreate}
                    disabled={disableCreateEdit}
                    variant="link"
                  >
                    Cancel
                  </Button>
                  <Button variant="primary" disabled={disableCreateEdit}>
                    {disableCreateEdit ? <Spinner /> : null}
                    {mode === "edit" ? "Save" : "Create"}
                  </Button>
                </SpaceBetween>
              )
            }
          >
            <SpaceBetween size="l">
              <Container
                header={<Header variant="h2">{cap(type)} information</Header>}
              >
                <br />
                <ColumnLayout columns={2} className={styles.horizontalDivider}>
                  <GaiaObjectName
                    country={country}
                    governmentEntity={governmentEntity}
                    localJurisdiction={localJurisdiction}
                    entity={entity}
                    noticeType={noticeType}
                    taxPeriodStartDate={taxPeriodStartDate}
                    taxPeriodEndDate={taxPeriodEndDate}
                    litigationType={litigationType}
                    category={type}
                    titleSuffix={titleSuffix}
                    entityOptions={
                      uiContent?.entityOptions as Array<{
                        label: string;
                        value: string;
                        description?: string;
                      }>
                    }
                    taxTypes={taxTypes}
                  />
                  <FormField
                    label={
                      <>
                        Name suffix - <i>optional</i>
                      </>
                    }
                    description="Additional information appended to name"
                    errorText={get(formState?.errors, `titleSuffix.message`)}
                  >
                    <ControlledInput name="titleSuffix" control={control} />
                  </FormField>
                </ColumnLayout>
                {customAttributes && (
                  <SpaceBetween size="l">
                    <div></div>
                    {extractUniqueGroups(customAttributes).map((groupName) => {
                      const groupAttributes = customAttributes.filter(
                        (attr: any) => (attr?.group || "") === groupName
                      );

                      return (
                        <AttributeGroupRenderer
                          key={groupName}
                          groupName={groupName}
                          groupAttributes={groupAttributes}
                          formState={formState}
                          control={control}
                          type={type}
                          mode={mode}
                          setValue={setValue}
                          filteredGovEntityOptions={filteredGovEntityOptions}
                        />
                      );
                    })}
                  </SpaceBetween>
                )}
              </Container>
              <Container header={<Header variant="h2">Contact</Header>}>
                <FormField
                  label="Select contact"
                  description="You can select up to 10 contacts"
                  errorText={get(formState?.errors, `contactIds.message`)}
                >
                  <ControlledMultiselect
                    name={"contactIds"}
                    control={control}
                    placeholder="Search by name or email"
                    filteringType="auto"
                    options={
                      contactsList?.contacts?.contacts.map((contact) => ({
                        value: contact.contactId,
                        label: contact.fullName,
                        description: contact.email,
                      })) ?? []
                    }
                  />
                </FormField>
              </Container>
              {mode === "create" && (
                <>
                  <Container header={<Header variant="h2">Templates</Header>}>
                    <FormField
                      label={
                        <>
                          Task groups and tasks will be created according to the
                          template. See{" "}
                          <Link href={`/team/?tabId=templates`}>
                            Team Settings
                          </Link>{" "}
                          for detailed information.
                        </>
                      }
                      errorText={get(formState?.errors, `template.message`)}
                    >
                      <ControlledSelect
                        name={"template"}
                        control={control}
                        filteringType="auto"
                        placeholder="Choose a template"
                        options={
                          !getTemplatesByCategory.has(teamName)
                            ? [
                                {
                                  value: undefined,
                                  label:
                                    "There are currently no templates defined for this team",
                                },
                              ]
                            : getTemplatesByCategory.get(teamName)
                        }
                      />
                    </FormField>
                  </Container>

                  {type === "notice" && (
                    <Container
                      header={
                        <Header variant="h2">
                          Upload notice -
                          <Box
                            variant="awsui-value-large"
                            fontWeight="bold"
                            fontSize="heading-l"
                          >
                            <i> optional</i>
                          </Box>
                        </Header>
                      }
                    >
                      <br />
                      <FormField>
                        <div>
                          <UploadFile setFiles={setFiles} files={files} />
                        </div>
                      </FormField>
                    </Container>
                  )}
                </>
              )}
            </SpaceBetween>
          </Form>
        </form>
      </PermissionBoundary>
    );
  }
);

export default CreateEditGaiaobjectForm;
